/**
 * Google Maps API Loader Utility
 *
 * Provides lazy loading of Google Maps JavaScript API with fallback detection.
 * Used by LocationAutocomplete and MapPicker components.
 */

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

// Track loading state
let loadPromise = null;
let isLoaded = false;

/**
 * Check if Google Maps API key is configured
 */
export const isGoogleMapsConfigured = () => {
  return !!GOOGLE_MAPS_API_KEY && GOOGLE_MAPS_API_KEY.length > 0;
};

/**
 * Check if Google Maps API is loaded and ready
 */
export const isGoogleMapsLoaded = () => {
  return isLoaded && typeof window !== 'undefined' && window.google?.maps?.places;
};

/**
 * Load Google Maps JavaScript API
 * Returns a promise that resolves when the API is ready
 *
 * @returns {Promise<google.maps>} Google Maps API object
 */
export const loadGoogleMapsAPI = () => {
  // Return existing promise if already loading
  if (loadPromise) {
    return loadPromise;
  }

  // Return immediately if already loaded
  if (isGoogleMapsLoaded()) {
    return Promise.resolve(window.google.maps);
  }

  // Check if API key is configured
  if (!isGoogleMapsConfigured()) {
    return Promise.reject(new Error('Google Maps API key not configured'));
  }

  // Create loading promise
  loadPromise = new Promise((resolve, reject) => {
    // Check if script already exists
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      // Script exists, wait for it to load
      if (window.google?.maps) {
        isLoaded = true;
        resolve(window.google.maps);
      } else {
        existingScript.addEventListener('load', () => {
          isLoaded = true;
          resolve(window.google.maps);
        });
        existingScript.addEventListener('error', () => {
          reject(new Error('Failed to load Google Maps API'));
        });
      }
      return;
    }

    // Create and inject script
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places&callback=__googleMapsCallback`;
    script.async = true;
    script.defer = true;

    // Set up callback
    window.__googleMapsCallback = () => {
      isLoaded = true;
      delete window.__googleMapsCallback;
      resolve(window.google.maps);
    };

    script.onerror = () => {
      loadPromise = null;
      delete window.__googleMapsCallback;
      reject(new Error('Failed to load Google Maps API'));
    };

    document.head.appendChild(script);
  });

  return loadPromise;
};

/**
 * Create a new Places Autocomplete session token
 * Session tokens bundle autocomplete requests for billing optimization
 *
 * @returns {google.maps.places.AutocompleteSessionToken|null}
 */
export const createSessionToken = () => {
  if (!isGoogleMapsLoaded()) {
    return null;
  }
  return new window.google.maps.places.AutocompleteSessionToken();
};

/**
 * Get place details from a place_id
 *
 * @param {string} placeId - Google Places place_id
 * @param {google.maps.places.AutocompleteSessionToken} sessionToken - Session token for billing
 * @returns {Promise<google.maps.places.PlaceResult>}
 */
export const getPlaceDetails = (placeId, sessionToken = null) => {
  return new Promise((resolve, reject) => {
    if (!isGoogleMapsLoaded()) {
      reject(new Error('Google Maps API not loaded'));
      return;
    }

    // Create a temporary div for PlacesService (required by API)
    const tempDiv = document.createElement('div');
    const service = new window.google.maps.places.PlacesService(tempDiv);

    const request = {
      placeId,
      fields: ['formatted_address', 'geometry', 'name', 'place_id', 'address_components'],
      ...(sessionToken && { sessionToken })
    };

    service.getDetails(request, (place, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
        resolve(place);
      } else {
        reject(new Error(`Places API error: ${status}`));
      }
    });
  });
};

export default {
  isGoogleMapsConfigured,
  isGoogleMapsLoaded,
  loadGoogleMapsAPI,
  createSessionToken,
  getPlaceDetails
};
